import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import API from '../../services/api';
import useEntityAlerts from '../../hooks/useEntityAlerts';
import './AdminRowAlerts.css';

const statusLabel = { active: 'Actif', pending: 'En attente', suspended: 'Suspendu' };
const statusClass = { active: 'badge-active', pending: 'badge-pending', suspended: 'badge-suspended' };

const PartenairesAdmin = () => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const [partners, setPartners]         = useState([]);
  const [counts, setCounts]             = useState({ pending: 0, active: 0, suspended: 0 });
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [modal, setModal]               = useState(null);
  const [commission, setCommission]     = useState('5');
  const [actionLoading, setActionLoading] = useState(false);

  const prevMonth = () => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().slice(0, 7);
  };
  const [billMonth, setBillMonth]   = useState(prevMonth);
  const [billLoading, setBillLoading] = useState(false);
  const [billError, setBillError]   = useState(null);

  const { alertMap, markSeen, markTreated, refresh: refreshAlerts } = useEntityAlerts('partner');
  const rowRefs          = useRef({});
  const lastHighlightId  = useRef(null);

  const fetchPartners = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (search) params.search = search;
      const res = await API.get('/admin/partners', { params });
      const list = res.data.partners || [];
      setPartners(list);
      setCounts({
        pending: list.filter(p => p.status === 'pending').length,
        active: list.filter(p => p.status === 'active').length,
        suspended: list.filter(p => p.status === 'suspended').length,
      });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, search]);

  useEffect(() => {
    const timer = setTimeout(fetchPartners, 300);
    return () => clearTimeout(timer);
  }, [fetchPartners]);

  // Scroll to + highlight the row when navigating from a notification
  useEffect(() => {
    if (loading) return;
    const highlight = location.state?.highlightEntity;
    if (!highlight || highlight.type !== 'partner') return;
    if (lastHighlightId.current === highlight.id) return;
    const el = rowRefs.current[highlight.id];
    if (el) {
      lastHighlightId.current = highlight.id;
      setTimeout(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('row-alert-highlight');
        markSeen(highlight.id);
        setTimeout(() => el.classList.remove('row-alert-highlight'), 1950);
      }, 100);
    }
  }, [loading, location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleApprove = async () => {
    setActionLoading(true);
    try {
      await API.put(`/admin/partners/${modal.partner.id}/approve`, { commission_rate: commission });
      markTreated(modal.partner.id);
      await fetchPartners();
      refreshAlerts();
      setModal(null);
    } catch (e) { console.error(e); }
    finally { setActionLoading(false); }
  };

  const handleReject = async () => {
    setActionLoading(true);
    try {
      await API.put(`/admin/partners/${modal.partner.id}/reject`);
      markTreated(modal.partner.id);
      await fetchPartners();
      refreshAlerts();
      setModal(null);
    } catch (e) { console.error(e); }
    finally { setActionLoading(false); }
  };

  const handleSuspend = async (id) => {
    try {
      await API.put(`/admin/partners/${id}/suspend`);
      markTreated(id);
      await fetchPartners();
    } catch (e) { console.error(e); }
  };

  const handleReactivate = async (id) => {
    try {
      await API.put(`/admin/partners/${id}/reactivate`);
      markTreated(id);
      await fetchPartners();
    } catch (e) { console.error(e); }
  };

  const downloadBilling = async () => {
    setBillError(null);
    setBillLoading(true);
    try {
      const res = await API.get('/admin/billing/export', {
        params: { month: billMonth },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv;charset=utf-8;' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `facturation-partenaires-${billMonth}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setBillError('Erreur lors de la génération du fichier.');
      console.error(e);
    } finally {
      setBillLoading(false);
    }
  };

  return (
    <div>
      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>
        {[
          { label: 'En attente de validation', value: counts.pending, color: '#f59e0b' },
          { label: 'Partenaires actifs', value: counts.active, color: '#10b981' },
          { label: 'Suspendus', value: counts.suspended, color: '#ef4444' },
        ].map((s, i) => (
          <div key={i} className="admin-table-card" style={{ padding: 20 }}>
            <div style={{ fontSize: 30, fontWeight: 700, color: s.color, marginBottom: 4 }}>{s.value}</div>
            <div style={{ fontSize: 13, color: '#64748b' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Billing export */}
      <div className="admin-table-card" style={{ padding: '18px 22px', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <i className="fas fa-file-invoice-dollar" style={{ color: '#06b6d4', fontSize: 18 }}></i>
            <span style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>Export facturation mensuelle</span>
            <span style={{ fontSize: 12, color: '#64748b' }}>— CSV pour virements partenaires</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginLeft: 'auto', flexWrap: 'wrap' }}>
            <label style={{ fontSize: 13, color: '#475569', fontWeight: 600, whiteSpace: 'nowrap' }}>Mois :</label>
            <input
              type="month"
              value={billMonth}
              onChange={e => { setBillMonth(e.target.value); setBillError(null); }}
              style={{
                padding: '7px 11px', borderRadius: 8,
                border: '1.5px solid #e2e8f0', fontSize: 13,
                color: '#0f172a', background: '#f8fafc',
                outline: 'none', cursor: 'pointer',
              }}
            />
            <button
              onClick={downloadBilling}
              disabled={billLoading || !billMonth}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 7,
                padding: '8px 18px',
                background: billLoading ? '#e2e8f0' : 'linear-gradient(135deg, #0891b2, #06b6d4)',
                color: billLoading ? '#94a3b8' : 'white',
                border: 'none', borderRadius: 8,
                fontSize: 13, fontWeight: 700,
                cursor: billLoading ? 'not-allowed' : 'pointer',
                boxShadow: billLoading ? 'none' : '0 4px 12px rgba(6,182,212,0.30)',
                transition: 'all 0.2s',
              }}
            >
              <i className={billLoading ? 'fas fa-spinner fa-spin' : 'fas fa-download'}></i>
              {billLoading ? 'Génération…' : 'Télécharger CSV'}
            </button>
            {billError && (
              <span style={{ fontSize: 12, color: '#dc2626', display: 'flex', alignItems: 'center', gap: 5 }}>
                <i className="fas fa-exclamation-circle"></i> {billError}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="admin-filters-bar">
        <input
          className="admin-filter-input"
          placeholder="Rechercher par nom, email..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select className="admin-filter-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">Tous les statuts</option>
          <option value="pending">En attente</option>
          <option value="active">Actif</option>
          <option value="suspended">Suspendu</option>
        </select>
        <button className="admin-btn-secondary" onClick={fetchPartners}>
          <i className="fas fa-sync-alt"></i> Actualiser
        </button>
      </div>

      {/* Table */}
      <div className="admin-table-card">
        {loading ? (
          <div style={{ padding: '40px 0', textAlign: 'center', color: '#94a3b8' }}>
            <i className="fas fa-spinner fa-spin" style={{ fontSize: 20, color: '#3b82f6', marginBottom: 8, display: 'block' }}></i>
            Chargement...
          </div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Entreprise</th>
                <th>Email</th>
                <th>Pays</th>
                <th>Commission</th>
                <th>Points wallet</th>
                <th>Date</th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {partners.map(p => {
                const alertStatus = alertMap[String(p.id)];
                const alertClass  = alertStatus === 'new'  ? 'row-alert-new'
                                  : alertStatus === 'seen' ? 'row-alert-seen'
                                  : '';
                return (
                <tr
                  key={p.id}
                  ref={el => { if (el) rowRefs.current[p.id] = el; }}
                  className={alertClass}
                  onClick={() => {
                    if (alertStatus === 'new') markSeen(p.id);
                    navigate('/dashboard/admin/transactions', {
                      state: { search: p.company_name, tab: 'b2c', from: 'partner', label: p.company_name }
                    });
                  }}
                  style={{ cursor: 'pointer' }}
                  title={`Voir les transactions de ${p.company_name}`}
                >
                  <td>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <strong>{p.company_name}</strong>
                      {alertStatus === 'new' && <span className="badge-new-alert">Nouveau</span>}
                      <i className="fas fa-history" style={{ fontSize: 10, color: '#94a3b8' }}></i>
                    </span>
                  </td>
                  <td style={{ color: '#64748b' }}>{p.email}</td>
                  <td>{p.country_code || '-'}</td>
                  <td style={{ color: '#10b981', fontWeight: 600 }}>{p.commission_rate ?? '-'}%</td>
                  <td>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <i className="fas fa-coins" style={{ color: '#f59e0b', fontSize: 11 }}></i>
                      {(p.available_points || 0).toLocaleString('fr')}
                    </span>
                  </td>
                  <td style={{ fontSize: 12, color: '#94a3b8' }}>{new Date(p.created_at).toLocaleDateString('fr')}</td>
                  <td><span className={statusClass[p.status] || 'badge-inactive'}>{statusLabel[p.status] || p.status}</span></td>
                  <td onClick={e => e.stopPropagation()}>
                    {p.status === 'pending' && (
                      <>
                        <button className="admin-btn-icon approve" title="Accepter" onClick={() => { markSeen(p.id); setCommission(p.commission_rate || '5'); setModal({ type: 'approve', partner: p }); }}>
                          <i className="fas fa-check"></i>
                        </button>
                        <button className="admin-btn-icon reject" title="Refuser" onClick={() => { markSeen(p.id); setModal({ type: 'reject', partner: p }); }}>
                          <i className="fas fa-times"></i>
                        </button>
                      </>
                    )}
                    {p.status === 'active' && (
                      <button className="admin-btn-icon suspend" title="Suspendre" onClick={() => handleSuspend(p.id)}>
                        <i className="fas fa-pause"></i>
                      </button>
                    )}
                    {p.status === 'suspended' && (
                      <button className="admin-btn-icon approve" title="Réactiver" onClick={() => handleReactivate(p.id)}>
                        <i className="fas fa-play"></i>
                      </button>
                    )}
                    <button
                      className="admin-btn-icon"
                      title="Voir les transactions"
                      onClick={() => navigate('/dashboard/admin/transactions', {
                        state: { search: p.company_name, tab: 'b2c', from: 'partner', label: p.company_name }
                      })}
                    >
                      <i className="fas fa-history"></i>
                    </button>
                  </td>
                </tr>
                );
              })}
              {partners.length === 0 && (
                <tr><td colSpan={8} style={{ textAlign: 'center', color: '#94a3b8', padding: '30px 0' }}>Aucun partenaire trouvé</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal Approve */}
      <AnimatePresence>
        {modal?.type === 'approve' && (
          <motion.div className="admin-modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={e => e.target === e.currentTarget && setModal(null)}>
            <motion.div className="admin-modal" initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}>
              <div className="admin-modal-header">
                <h2>Accepter le partenaire</h2>
                <button className="admin-modal-close" onClick={() => setModal(null)}><i className="fas fa-times"></i></button>
              </div>
              <p style={{ color: '#64748b', fontSize: 14, marginBottom: 20 }}>
                Vous acceptez <strong>{modal.partner.company_name}</strong> ({modal.partner.email}).
              </p>
              <div className="admin-form-group">
                <label>Commission personnalisée (%)</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input type="number" className="admin-form-input" value={commission} onChange={e => setCommission(e.target.value)} min="0" max="100" step="0.1" />
                  <span style={{ color: '#64748b' }}>%</span>
                </div>
              </div>
              <div className="admin-modal-actions">
                <button className="btn-accept" onClick={handleApprove} disabled={actionLoading}>
                  {actionLoading ? 'En cours...' : 'Accepter le partenaire'}
                </button>
                <button className="btn-cancel" onClick={() => setModal(null)}>Annuler</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal Reject */}
      <AnimatePresence>
        {modal?.type === 'reject' && (
          <motion.div className="admin-modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={e => e.target === e.currentTarget && setModal(null)}>
            <motion.div className="admin-modal" initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}>
              <div className="admin-modal-header">
                <h2>Refuser le partenaire</h2>
                <button className="admin-modal-close" onClick={() => setModal(null)}><i className="fas fa-times"></i></button>
              </div>
              <p style={{ color: '#64748b', fontSize: 14, marginBottom: 20 }}>
                Refuser et supprimer définitivement <strong>{modal.partner.company_name}</strong> ?
              </p>
              <div className="admin-form-group">
                <label>Motif du refus</label>
                <select className="admin-form-select">
                  <option>Documents incomplets</option>
                  <option>Informations non conformes</option>
                  <option>Activité non éligible</option>
                  <option>Autre motif</option>
                </select>
              </div>
              <div className="admin-modal-actions">
                <button className="btn-reject" onClick={handleReject} disabled={actionLoading}>
                  {actionLoading ? 'En cours...' : 'Confirmer le refus'}
                </button>
                <button className="btn-cancel" onClick={() => setModal(null)}>Annuler</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PartenairesAdmin;
